<template>
  <div v-if="!loading && !viewIsLoading">
    <b-modal
      id="modal-permissions"
      :title="$t('messages.permissions')"
      hide-footer
      body-class="pt-1"
    >
      <div v-for="(role, i) in rolesPermissions" :key="i">
        <div class="d-flex align-items-center mt-2">
          <font-awesome-icon
            :icon="faAngleRight"
            class="mr-1 mt-1 text-primary"
          />
          <h4 class="text-primary mb-0">{{ role.role }}</h4>
        </div>
        <div
          v-for="(permissions, index) in role.permissions"
          :key="index"
          class="d-flex align-items-start mt-2 ml-1"
        >
          <div class="d-flex align-items-center mr-2">
            <font-awesome-icon :icon="faCircle" class="dot mr-1" />
            <span class="font-weight-bold">{{ permissions.type }}: </span>
          </div>
          <span>{{ permissions.desc }}</span>
        </div>
      </div>
    </b-modal>
    <b-form-group :label="$t('field.name')">
      <b-form-input
        v-model="item.name"
        name="name"
        type="text"
        required
        placeholder="Lorem ipsum set dolor"
      />
    </b-form-group>
    <b-row>
      <b-col>
        <b-form-group :label="$t('field.phone')">
          <b-form-input
            v-model="item.phone"
            name="phone"
            type="number"
            required
            placeholder="123456789"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="$t('field.email')">
          <b-form-input
            v-model="item.email"
            name="email"
            type="email"
            required
            placeholder="lorem@ipsum.dolor"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group :label="$t('field.roles')">
          <div class="d-flex align-items-center">
            <multi-select
              v-model="role"
              :multiple="false"
              :options="roles"
              :allow-empty="false"
              :searchable="true"
              :placeholder="$t('actions.pick_role')"
              :show-labels="false"
              label="label"
              track-by="label"
              @input="updateRole"
            >
              <span slot="noOptions">{{ $t("messages.no_results") }}</span>
              <span slot="noResult">{{ $t("messages.no_results") }}</span>
            </multi-select>
            <b-button
              variant="unstyled"
              v-b-modal.modal-permissions
              class="
                bg-purple-light
                text-white
                rounded-circle
                modal-button
                h-50
                ml-2
              "
              >?</b-button
            >
          </div>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="$t('field.companies')">
          <multi-select
            v-model="companies"
            :multiple="true"
            :options="companiesList"
            :allow-empty="false"
            :searchable="true"
            :placeholder="$t('actions.pick_companies')"
            :show-labels="false"
            label="name"
            track-by="id"
            @input="updateCompanies"
          >
            <span slot="noOptions">{{ $t("messages.no_results") }}</span>
            <span slot="noResult">{{ $t("messages.no_results") }}</span>
          </multi-select>
        </b-form-group>
      </b-col>
    </b-row>
    <b-form-group :label="$t('field.department')">
      <b-form-input
        v-model="item.department"
        name="department"
        type="text"
        required
        placeholder="Lorem ipsum set dolor"
      />
    </b-form-group>
    <b-form-group :label="$t('field.signature')">
      <div class="d-flex align-items-center">
        <image-uploader
          :max-width="1920"
          className="d-none"
          :quality="1"
          :auto-rotate="true"
          :preview="false"
          outputFormat="verbose"
          accept="image/*"
          do-not-resize="['gif', 'svg']"
          @input="imageUploaded"
        >
          <label
            for="fileInput"
            slot="upload-label"
            class="
              d-inline-flex
              align-items-center
              justify-content-between
              mb-0
            "
          >
            <div
              class="
                choose-signature-btn
                rounded
                px-5
                py-2
                fs-14
                shadow-sm
                border border-primary
                text-primary
                align-items-center
                justify-content-center
              "
            >
              <span>{{ $t("actions.pick_signature") }}</span>
            </div>
          </label>
        </image-uploader>
        <b-button
          v-if="signature"
          variant="unstyled"
          @click.stop="removeImage"
          class="
            d-flex
            align-items-center
            justify-content-center
            ml-1
            bg-gray-600
            text-white
            rounded-pill
            fs-12
            text-
          "
          >{{ $t("messages.remove_image") }}
          <font-awesome-icon :icon="faTimesCircle" class="ml-1 text-white"
        /></b-button>
      </div>
      <div class="mt-4">
        <img :src="signature" class="w-auto mw-100" />
      </div>
    </b-form-group>
  </div>
  <div v-else>
    <spinner />
  </div>
</template>

<script>
import {
  faCircle,
  faAngleRight,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import rolesApi from '@/api/roles'
import companiesApi from '@/api/companies'
import { rolesPermissions } from '@/constants/rolesPermissions'
import { mapGetters } from 'vuex'
import model from '@/models/users'
import edit from '@/mixins/edit'
import MultiSelect from 'vue-multiselect'

export default {
  props: {
    viewIsLoading: {
      type: Boolean,
      required: true
    }
  },
  components: {
    MultiSelect,
    FontAwesomeIcon
  },
  mixins: [edit],
  data: () => ({
    model: JSON.parse(JSON.stringify(model.empty)),
    faTimesCircle,
    rolesApi,
    companiesApi,
    rolesPermissions,
    faCircle,
    faAngleRight,
    loading: true,
    roles: [],
    role: null,
    companiesList: [],
    companies: null,
    signature: null
  }),
  computed: {
    ...mapGetters({
      item: 'form/fields'
    })
  },
  methods: {
    async init() {
      await Promise.all([this.getRoles(), this.getCompanies()])

      this.setRole()
      this.setCompanies()
      this.setSignature()

      this.loading = false
    },
    async getRoles() {
      try {
        const response = await this.rolesApi.all()

        if (response && response.data && response.data.list) {
          this.roles = response.data.list.map(item => ({
            label: item
          }))
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }
    },
    async getCompanies() {
      try {
        const response = await this.companiesApi.all()

        if (response && response.data && response.data.list) {
          this.companiesList = response.data.list
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }
    },
    setRole() {
      if (!this.item || !this.item.roles || !this.item.roles[0]) {
        return
      }

      this.role = this.item.roles
        ? this.roles.find(item => item.label === this.item.roles[0].name)
        : null

      this.updateRole(this.role.label)
    },
    setCompanies() {
      this.companies = this.item.companies ? this.item.companies : []

      if (this.companies === undefined) {
        this.$toasted.error(
          this.$t('messages.not_found_foreign', {
            field: this.$t('field.companies').toLowerCase()
          })
        )
      } else {
        this.updateCompanies(this.companies)
      }
    },
    setSignature() {
      if (this.item.file) {
        this.signature = this.item.file
      }
    },
    updateRole(role) {
      this.item.role = role
    },
    async updateCompanies(data) {
      await this.$set(
        this.item,
        'companies',
        this.companies && this.companies.length > 0
          ? this.companies.map(item => item.id).join(',')
          : ''
      )

      this.emitCompaniesInfo(data)
    },
    imageUploaded(file) {
      this.setImage(file.dataUrl)
      this.signature = file.dataUrl
      this.item.file = this.model.file
    },
    removeImage() {
      this.signature = null
      this.item.file = ''
    },
    emitCompaniesInfo(data) {
      this.$emit('companiesInfo', data)
    }
  }
}
</script>
<style lang="scss" scoped>
.dot {
  font-size: 0.3rem;
}

.modal-button {
  font-size: 0.75rem;
}

.choose-signature-btn {
  cursor: pointer;
}
</style>